import { useMemo } from "react";
import FlowChart, {
  FlowChartData,
  FlowChartEdge,
  FlowChartNode,
} from "../../components/FlowChart";
import { ChallengeDetails } from "../../types/cityEscape";

export interface EnigmasFlowChartProps {
  enigmas?: ChallengeDetails[];
  isLoading?: boolean;
  selectedId?: string;
  onSelection?: (node?: ChallengeDetails) => void;
}

export function EnigmasFlowChart({
  enigmas,
  isLoading,
  selectedId,
  onSelection,
}: EnigmasFlowChartProps) {
  const flowChartData: FlowChartData = useMemo(() => {
    if (isLoading) {
      return {
        nodes: [
          {
            id: "loading",
            label: "Caricamento...",
            type: "loading",
            data: { subtitle: "Recupero enigmi" },
          },
        ],
        edges: [],
      };
    } else if (!enigmas || enigmas.length === 0) {
      return {
        nodes: [
          {
            id: "placeholder",
            label: "Nessun enigma trovato",
            type: "default",
            data: {
              subtitle: "Nessun dato disponibile",
            },
          },
        ],
        edges: [],
      };
    } else {
      return buildEnigmasGraph(enigmas);
    }
  }, [enigmas, isLoading]);

  console.debug("Flow chart data:", flowChartData);

  return (
    <FlowChart
      data={flowChartData}
      height={"75vh"}
      direction="DOWN"
      nodeSpacing={80}
      layerSpacing={80}
      selectedNodeId={selectedId}
      onSelection={(node) => onSelection?.(node?.data?.originalData as any)}
      defaultZoom={0.8}
    />
  );
}

export function buildEnigmasGraph(
  enigmas: ChallengeDetails[] | undefined
): FlowChartData {
  if (!enigmas?.length) {
    return { nodes: [], edges: [] };
  }

  // Create nodes from enigmas datasource
  const nodes: FlowChartNode[] = enigmas.map(buildEnigmaNode);
  const edges: FlowChartEdge[] = enigmas.flatMap(buildEnigmaEdges);
  return setupFlowChartBoundaries({ nodes, edges });
}

export function buildEnigmaNode(
  enigma: ChallengeDetails,
  index: number
): FlowChartNode {
  return {
    id: enigma.id,
    label: enigma.label,
    type: "enigma",
    data: {
      subtitle: `Enigma ${index + 1}`,
      originalData: enigma,
      tooltip: enigma.textContent,
    },
  };
}

export function buildEnigmaEdges(enigma: ChallengeDetails): FlowChartEdge[] {
  return enigma.meta.next
    .filter(({ id: target }) => target && target.trim())
    .map(({ id: target, isAlternative }) => ({
      id: `${enigma.id}->${target}`,
      source: enigma.id,
      target: target,
      type: isAlternative || enigma.scene.isAlternative ? "dashed" : "solid",
    }));
}

export function addStartingNode(data: FlowChartData) {
  const startNode: FlowChartNode = {
    id: "start",
    label: "Start",
    type: "start",
    data: { subtitle: "Start of the flow", originalData: null },
  };

  data.nodes.unshift(startNode);

  // Connect start node to the first enigma
  if (data.edges.length > 0) {
    const firstEdge = data.edges[0];
    data.edges.unshift({
      id: `start->${firstEdge.source}`,
      source: "start",
      target: firstEdge.source,
      type: "solid",
    });
  }

  return data;
}

export function setupFlowChartBoundaries(data: FlowChartData) {
  addStartingNode(data);
  addEndingNode(data);
  return data;
}

export function addEndingNode(data: FlowChartData) {
  const endNode: FlowChartNode = {
    id: "end",
    label: "Completamento",
    type: "success",
    data: { subtitle: "Fine del gioco", originalData: null },
  };

  const endNodes = data.nodes.filter((node) => {
    const isTarget = data.edges.some((edge) => edge.target === node.id);
    const isSource = data.edges.some((edge) => edge.source === node.id);
    return isTarget && !isSource && node.id !== "end";
  });

  if (endNodes.length > 0) {
    data.nodes.push(endNode);
    endNodes.forEach((endNode) => {
      const isAlternativePath = data.edges
        .filter((e) => e.target === endNode.id)
        .every((e) => e.type === "dashed");

      data.edges.push({
        id: `${endNode.id}-end`,
        source: endNode.id,
        type: isAlternativePath ? "dashed" : "solid",
        target: "end",
        label: "Completa",
      });
    });
  }

  return data;
}
